import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Button, Tabs, Tab } from "@mui/material";
import { StyledDataGrid } from "./styles";
import {
    GridActionsCellItem
} from "@mui/x-data-grid";
import {
    //Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    PictureAsPdf as DownloadPDF
} from "@mui/icons-material";

//Components
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import IconsWithTooltip from "../../components/Icons/IconsWithTooltip";
import DownloadProgressDialog from "../../components/Dialog/DownloadProgressDialog";
import InformationDialog from "../../components/Dialog/InformationDialog";
import CustomToolbar from "../../components/Toolbar/CustomToolbar";

//utils
import { getFormattedDate } from '../../utils/getFormattedDateTime';
import { getLatestYearRank } from "../../utils/getLatestYearRank";

//custom hook
import { useFetch } from "../../hooks/useFetch";

//services
import { downloadMitigationPlans } from "../../services/PdfDownloadService";
import AuthToken from "../../services/AuthToken";

//constants
import { API_URLS } from "../../Constants";

//context
import { useAuth } from "../../context/AuthContext";

const pathItems = [
    "Dashboard",
    "View Roadways"
]

export default function ViewRoadways() {
    const [roadwaysData, setRoadwaysData] = useState([]);
    const [roadwaysWithNoProjects, setRoadwaysWithNoProjects] = useState([]);
    const fetcher = useFetch("get", API_URLS["API_GET_ALL_ROADWAYS"]);
    const [downloadProgressDialog, setDownloadProgressDialog] = useState({ open: false });
    const downloadAbortRef = useRef(null);
    const [informationDialog, setInformationDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: () => { },
    });
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState({ column: '', value: '' });

    //Fetch data from API when the component renders.
    useEffect(() => {
        fetcher.setRequest({});
    }, []);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = fetcher.serverResponse;
        if (response?.success && response?.data) {
            if (response.data?.allRoadways?.length > 0) {
                const roadwaysData = response.data.allRoadways;

                //Sort alphabetically by roadway title
                const sortedRoadways = roadwaysData.toSorted((a, b) => a.roadwayTitle.localeCompare(b.roadwayTitle));
                setRoadwaysData(sortedRoadways || []);
            }
            if (response.data?.top105RoadwaysWithNoProjects?.length > 0) {

                //Sort alphabetically by roadway title
                const sortedRoadwaysWithNoProjects = response.data.top105RoadwaysWithNoProjects.toSorted((a, b) => a.roadwayTitle.localeCompare(b.roadwayTitle));
                setRoadwaysWithNoProjects(sortedRoadwaysWithNoProjects || []);
            }
        }
    }, [fetcher.serverResponse]);

    //Cancel any in-flight download when the page unmounts.
    useEffect(() => {
        return () => { downloadAbortRef.current?.abort(); };
    }, []);

    //Filter the rows based on the active filter value and column.
    const filteredRows = useMemo(() => {
        if (!roadwaysData) return [];
        if (!activeFilter.value || !activeFilter.column) {
            return roadwaysData;
        }
        const searchTerm = activeFilter.value.toLowerCase();
        const column = activeFilter.column;

        return roadwaysData.filter(row => {
            let rawValue = row[column];

            //handle region object to filter based on region name instead of id.
            if (column === 'region')
                rawValue = row.region?.name || "";

            //convert rank and previous ranks to integer and then compare
            else if (column === 'rank' || column === 'previousRanks' || column === 'numOfProjects') {
                const displayValue = column === 'previousRanks'
                    ? getLatestYearRank(row.previousRanks)
                    : row[column];
                const numericValue = parseInt(displayValue, 10);
                const numericSearch = parseInt(searchTerm, 10);
                if (isNaN(numericSearch)) return true;
                return numericValue === numericSearch;
            }
            else if (column === 'lastModified') {
                const date = row.lastModifiedDate ? getFormattedDate(new Date(row.lastModifiedDate)) : "";
                const user = row.lastModifiedBy ? row.lastModifiedBy.split('@')[0] : "";
                return `${date} ${user}`.toLowerCase().includes(searchTerm);
            }
            if (rawValue == null) return false;
            return String(rawValue).toLowerCase().includes(searchTerm);
        });
    }, [roadwaysData, activeFilter]);

    //Tab state
    const [activeTab, setActiveTab] = useState(0);

    //Filter the rows for roadways with no projects
    const filteredRoadwaysWithNoProjects = useMemo(() => {
        if (!roadwaysWithNoProjects) return [];
        if (!activeFilter.value || !activeFilter.column) {
            return roadwaysWithNoProjects;
        }
        const searchTerm = activeFilter.value.toLowerCase();
        const column = activeFilter.column;
        return roadwaysWithNoProjects.filter(row => {
            let rawValue = row[column];
            if (column === 'region')
                rawValue = row.region?.name || "";
            else if (column === 'rank' || column === 'previousRanks' || column === 'numOfProjects') {
                const displayValue = column === 'previousRanks'
                    ? getLatestYearRank(row.previousRanks)
                    : row[column];
                const numericValue = parseInt(displayValue, 10);
                const numericSearch = parseInt(searchTerm, 10);
                if (isNaN(numericSearch)) return true;
                return numericValue === numericSearch;
            }
            else if (column === 'lastModified') {
                const date = row.lastModifiedDate ? getFormattedDate(new Date(row.lastModifiedDate)) : "";
                const user = row.lastModifiedBy ? row.lastModifiedBy.split('@')[0] : "";
                return `${date} ${user}`.toLowerCase().includes(searchTerm);
            }
            if (rawValue == null) return false;
            return String(rawValue).toLowerCase().includes(searchTerm);
        });
    }, [roadwaysWithNoProjects, activeFilter]);

    //React Hooks
    const [selectedRows, setSelectedRows] = useState([]);
    const [confirmationDialog, setConfirmationDialog] = useState({ open: false });

    //Get loggedIn user details
    const { auth } = useAuth();
    const loggedInUser = auth?.user?.privileges || null;
    if (!loggedInUser) return;
    const showEditAction = loggedInUser?.roadways.includes("update");
    //const showDelAction = loggedInUser?.roadways.includes("delete");
    const showViewAction = loggedInUser?.roadways.includes("view");

    //when download pdf button is clicked
    const onClickDownloadPDF = async (segmentIds) => {
        if (!segmentIds?.length) return;

        if (segmentIds.length > 100) {
            setInformationDialog({
                open: true,
                title: "Error",
                message: 'Please select upto 100 segments at a time.',
                onClose: () => {
                    setInformationDialog((prevState) => ({ ...prevState, open: false }));
                }
            });
            return;
        }

        //Redirect to login if the token has expired.
        if (!AuthToken(new Date())) {
            sessionStorage.clear();
            navigate('/login');
            return;
        }

        downloadAbortRef.current?.abort();
        const controller = new AbortController();
        downloadAbortRef.current = controller;

        const isBulk = segmentIds.length > 1;
        setDownloadProgressDialog({
            open: true,
            message: isBulk ? 'Generating mitigation plan PDFs' : 'Downloading file',
            completed: isBulk ? 0 : undefined,
            total: isBulk ? segmentIds.length : undefined
        });

        try {
            //POST -> poll job status -> download zip (or single PDF inline)
            await downloadMitigationPlans(segmentIds, {
                signal: controller.signal,
                onProgress: ({ completed, total, status }) => {
                    setDownloadProgressDialog({
                        open: true,
                        message: status === 'done' ? 'Downloading file' : 'Generating mitigation plan PDFs',
                        completed,
                        total
                    });
                }
            });
            setDownloadProgressDialog((prevState) => ({ ...prevState, open: false }));
        } catch (error) {
            setDownloadProgressDialog((prevState) => ({ ...prevState, open: false }));
            if (error?.name === 'AbortError') return;
            console.error('Error downloading PDF:', error);
            setInformationDialog({
                open: true,
                title: "Error",
                message: error?.message || 'Some error occured. Please try again.',
                onClose: () => {
                    setInformationDialog((prevState) => ({ ...prevState, open: false }));
                }
            });
        } finally {
            if (downloadAbortRef.current === controller) downloadAbortRef.current = null;
        }
    }

    //Data Grid columns
    const columns = [
        {
            field: 'segmentId',
            headerName: 'Segment ID',
            flex: 1,
            valueGetter: (params) => parseInt(params.row.segmentId) || 0,
            renderCell: (params) => {
                const segmentIdValue = params.row.segmentId ? parseInt(params.row.segmentId) : 0;
                return (
                    <Typography variant="body2">
                        {segmentIdValue}
                    </Typography>
                );
            }
        },
        {
            field: "roadwayTitle",
            headerName: "Roadway Title",
            flex: 2,
            renderCell: (params) => {

                const handleTitleClick = (segmentId) => {
                    const backendPageUrl = `${window.location.origin}/pages/roadway/${segmentId}`;

                    // Open the backend URL in a new tab
                    window.open(backendPageUrl, "_blank");
                };

                return (
                    <Button
                        onClick={() => handleTitleClick(params.row.segmentId)}
                        aria-label={`Open details for ${params.row.roadwayTitle} in a new tab`}
                        variant="text"
                        sx={{
                            fontSize: "16px",
                            textTransform: "none",
                            minWidth: 0,
                            maxWidth: "100%",
                            whiteSpace: "normal",
                            textAlign: "left",
                            lineHeight: 1.4,
                            padding: 0,
                            paddingTop: '6px',
                            paddingBottom: '6px',
                            justifyContent: "flex-start",
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                            '&:focus': {
                                outline: '2px solid #000',
                            }
                        }}
                    >
                        <Typography variant="body2" sx={{ textAlign: "left", whiteSpace: "normal" }}>
                            {params.row.roadwayTitle}
                        </Typography>
                    </Button>
                );
            },
        },
        {
            field: 'region',
            headerName: 'Region(s)',
            flex: 1,
            valueGetter: (params) => params.row.region?.name || "None",
            renderCell: (params) => {
                const regionName = params.row.region && ('name' in params.row.region) ? params.row.region['name'] : "None"
                return (
                    <Typography variant="body2" key={params.id}>
                        {regionName}
                    </Typography>
                );
            }
        },
        {
            field: 'numOfProjects',
            headerName: 'Number of Projects',
            flex: 1,
            valueGetter: (params) => params.row.numOfProjects || 0,
            renderCell: (params) => {
                const numOfProjects = params.row.numOfProjects ? parseInt(params.row.numOfProjects) : 0;
                return (
                    <Typography variant="body2">
                        {numOfProjects}
                    </Typography>
                );
            }
        },
        {
            field: 'rank',
            headerName: 'Rank',
            flex: 1,
            valueGetter: (params) => parseInt(params.row.rank) || null,
            renderCell: (params) => {
                const rankValue = params.row.rank ? parseInt(params.row.rank) : null;
                return (
                    <Typography variant="body2">
                        {rankValue !== null ? rankValue : 0}
                    </Typography>
                );
            }
        },
        {
            field: 'previousRanks',
            headerName: 'Previous Rank',
            flex: 1,
            renderCell: (params) => {
                const previousYearRank = getLatestYearRank(params.row.previousRanks);
                return (
                    <Typography variant="body2">
                        {previousYearRank}
                    </Typography>
                );
            },
            valueGetter: (params) => {
                return getLatestYearRank(params.row.previousRanks);
            }
        },
        {
            field: "lastModified",
            headerName: "Last Modified",
            flex: 1,
            renderCell: (params) => {
                const lastModifiedDate = getFormattedDate(new Date(params.row.lastModifiedDate));
                const lastModifiedBy = params.row.lastModifiedBy ? params.row.lastModifiedBy.split('@')[0] || params.row.lastModifiedBy : "";
                return (
                    <div style={{ whiteSpace: 'normal' }}>
                        <Typography variant="body2">
                            {lastModifiedDate}
                        </Typography>
                        <Typography variant="body2">
                            {lastModifiedBy}
                        </Typography>
                    </div>
                );
            },
            valueGetter: (params) => {

                // Return a combined value that contains both date and last modified by
                const lastModifiedDate = new Date(params.row.lastModifiedDate);
                const lastModifiedBy = params.row.lastModifiedBy ? params.row.lastModifiedBy.split('@')[0] || params.row.lastModifiedBy : "";
                const formattedDate = getFormattedDate(lastModifiedDate);
                return `${formattedDate} ${lastModifiedBy}`;
            },
            sortComparator: (v1, v2) => {
                const [date1, user1] = v1.split(" "); // Split into date and user
                const [date2, user2] = v2.split(" "); // Split into date and user

                const parsedDate1 = new Date(date1); // Parse the date part for sorting
                const parsedDate2 = new Date(date2); // Parse the date part for sorting

                // First compare by date
                if (!isNaN(parsedDate1) && !isNaN(parsedDate2)) {
                    if (parsedDate1.getTime() !== parsedDate2.getTime()) {
                        return parsedDate1 - parsedDate2; // Sort by date
                    }
                }

                // If dates are the same, compare by lastModifiedBy (username)
                if (user1 < user2) {
                    return -1; // user1 comes before user2
                }
                if (user1 > user2) {
                    return 1; // user1 comes after user2
                }

                return 0; // If both date and user are the same
            }
        },
        {
            field: "actions",
            headerName: "Actions",
            type: "actions",
            width: 150,
            filterable: false,
            export: false,
            getActions: (params) => {
                const actions = [];
                const roadName = params.row.roadwayTitle;
                if (showViewAction) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<IconsWithTooltip title="View Roadway Details" icon={<ViewIcon />} />}
                            label={`View details for ${roadName}`}
                            onClick={() => { navigate(`/view-selected-roadway`, { state: { segmentId: params.row.segmentId } }) }}
                        />
                    )
                }
                if (showEditAction) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<IconsWithTooltip title="Edit Roadway Details" icon={<EditIcon />} />}
                            label={`Edit details for ${roadName}`}
                            onClick={() => { navigate(`/edit-roadway`, { state: { segmentId: params.row.segmentId, navFromView: true } }) }}
                        />
                    )
                }
                actions.push(
                    <GridActionsCellItem
                        icon={<IconsWithTooltip title="Download Mitigation Plans PDF" icon={<DownloadPDF />} />}
                        label={`Download Mitigation Plans PDF for ${roadName}`}
                        onClick={() => { onClickDownloadPDF([params.row.segmentId]) }}
                    />
                )
                /*if (showDelAction) {
                    actions.push(
                        <GridActionsCellItem
                            aria-label="Delete Action"
                            icon={<IconsWithTooltip title={`Delete ${roadName}`} icon={<DeleteIcon />} />}
                            label={`Delete ${roadName}`}
                            onClick={() => handleDeleteSelected([params.row.segmentId])}
                            showInMenu
                        />
                    )
                }*/
                return actions;
            },
        },
    ];

    /*Roadway Deletion: Set action on clicking "Yes" for selected roadway(s).
    const handleDeleteConfirm = () => {
        setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
    };

    //Roadway Deletion: Set ConfirmationDialog properties for selected roadway(s).
    const handleDeleteSelected = (selectedIds) => {
        if (selectedIds && selectedIds.length > 0) {
            setConfirmationDialog({
                open: true,
                title: "Delete Confirmation",
                message: `Are you sure you want to delete the selected roadway(s) ?`,
                onConfirm: () => {
                    const updatedRows = roadwaysData.filter(row => !selectedIds.includes(row.segmentId));

                    //Update data table.
                    setRoadwaysData(updatedRows);

                    //Update data in the global context.
                    updateData(updatedRows);
                    setSelectedRows([]);
                    handleDeleteConfirm();
                },
            });
        }
    };*/
    return (
        <>
            {/* Breadcrumbs for the page */}
            <Grid item xs={12} md={12}>
                <RouterBreadcrumbs pathItems={pathItems} />
            </Grid>

            {/* Page Title for the page */}
            <Grid item xs={12} md={12}>
                <Title title="View Roadways" />
            </Grid>

            {/* Render Delete All button 
            <Grid item xs={12}>
                {selectedRows.length > 1 && (
                    <StyledTopButton
                        aria-label="Delete All Button"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteSelected(selectedRows)}
                    >
                        Delete All
                    </StyledTopButton>
                )}
            </Grid>*/}

            {/* Tabs */}
            <Grid item xs={12}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => { setActiveTab(newValue); setSelectedRows([]); setActiveFilter({ column: '', value: '' }); }}
                    aria-label="Roadways tabs"
                >
                    <Tab label={`Top 105 With No Projects`} />
                    <Tab label={`All Roadways`} />
                </Tabs>
            </Grid>

            {/* Display Data Table */}
            <Grid item xs={12} sx={{ paddingRight: 3 }}>
                <StyledDataGrid
                    key={`grid-tab-${activeTab}`}
                    autoFocus
                    aria-label={activeTab === 0
                        ? "Top 105 Roadways with No Projects table. Use arrow keys to navigate through the table."
                        : "All Roadways table. Use arrow keys to navigate through the table."}
                    rows={(activeTab === 0 ? filteredRoadwaysWithNoProjects : filteredRows) || []}
                    columns={columns}
                    disableColumnMenu
                    loading={fetcher.isLoading}
                    getRowId={(row) => row.segmentId}
                    autoHeight
                    getRowHeight={() => 'auto'}
                    initialState={{
                        columns: {
                            sorting: {
                                sortModel: [{ field: 'lastModified' }],
                            },
                        },
                        pagination: { paginationModel: { page: 0, pageSize: 10 } },
                    }}
                    pageSizeOptions={[10, 15, 20, 25]}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        toolbar: {
                            totalCount: (activeTab === 0 ? roadwaysWithNoProjects : roadwaysData)?.length || 0,
                            filter: {
                                rowsCount: (activeTab === 0 ? filteredRoadwaysWithNoProjects : filteredRows).length,
                                columns: columns,
                                active: activeFilter,
                                onChange: (val) => setActiveFilter(val)
                            },
                            exportedFilename: activeTab === 0 ? "Top105_Roadways_No_Projects" : "All_Roadways",
                            otherBtns: selectedRows?.length > 1 ? (
                                <Button sx={{ fontSize: "13px" }} startIcon={<DownloadPDF />} onClick={() => { onClickDownloadPDF(selectedRows) }}>Download Selected PDF</Button>
                            ) : null
                        },
                        pagination: {
                            labelRowsPerPage: 'Rows per page:',
                            SelectProps: {
                                native: true,
                                inputProps: { 'aria-label': 'Rows per page selection', 'id': 'pagination-select-input' }
                            }
                        },
                        baseCheckbox: {
                            inputProps: { 'aria-label': 'Select row' }
                        }
                    }}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel) => setSelectedRows(newRowSelectionModel)}
                    selectedRows={selectedRows}
                />
            </Grid>

            {/* ConfirmationDialog component */}
            <ConfirmationDialog
                open={confirmationDialog.open}
                onClose={() =>
                    setConfirmationDialog((prevState) => ({ ...prevState, open: false }))
                }
                title={confirmationDialog.title}
                message={confirmationDialog.message}
                onConfirm={confirmationDialog.onConfirm}
            />

            {/* Download Progress Dialog */}
            <DownloadProgressDialog
                open={downloadProgressDialog.open}
                message={downloadProgressDialog.message}
                completed={downloadProgressDialog.completed}
                total={downloadProgressDialog.total}
            />

            {/* Information Dialog component */}
            <InformationDialog
                open={informationDialog.open}
                title={informationDialog.title}
                message={informationDialog.message}
                onClose={informationDialog.onClose}
            />
        </>
    );
}